import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { catchError, forkJoin, interval, map, of, startWith } from 'rxjs';
import { ApiService } from '@services/api.service';
import { SocketService } from '@services/socket.service';
import { ApiRequest } from '@enums/api.enum';
import {
	Kierunek,
	KorektaZywa,
	OverbalanceResponse,
	PROG_BLISKO,
	Rama,
	SEKUNDY_RAMY,
	StanZywy,
	SwiecaZywa,
	TrendWidok,
	korektaNaZywo,
} from './overbalance.model';

const KLUCZ = 'cr-overbalance-pomiary';
/** Lekka analiza (sam bieżący trend) — co minutę; cache API trzyma ją do nowej świecy. */
const ODSWIEZ_MS = 60_000;
const MAX_POMIAROW = 10;
const MAX_WPISOW = 200;

export type TypWpisu =
	| 'start'
	| 'blisko'
	| 'przebicie'
	| 'cofniecie'
	| 'rekord_ceny'
	| 'rekord_czasu'
	| 'nowy_szczyt'
	| 'zagrozenie'
	| 'koniec_trendu'
	| 'nowy_trend'
	| 'stop';

export interface WpisPomiaru {
	/** Unix ms. */
	t: number;
	typ: TypWpisu;
	cena: number;
	/** Potwierdzone zamknięciem świecy (false = w trakcie świecy). */
	potw: boolean;
	/** Liczby do zdania: zużycie ceny / czasu (ułamki), głębokość %, kierunek trendu. */
	zc?: number | null;
	zt?: number | null;
	g?: number;
	kier?: Kierunek;
}

/** Stan z ostatniej obserwacji — z nim porównujemy kolejną, żeby zapisać zmiany. */
interface Migawka {
	trendOd: number | null;
	kierunek: Kierunek | null;
	korektaOd: number | null;
	obCena: number | null;
	obCzas: number | null;
	przebita: boolean;
	zagrozenie: boolean;
	blisko: boolean;
}

export interface Pomiar {
	id: string;
	symbol: string;
	rama: Rama;
	/** Unix ms. */
	start: number;
	koniec: number | null;
	startCena: number;
	/** Korekta w chwili startu (zużycie rekordu ceny / czasu, głębokość, stan). */
	startZc: number | null;
	startZt: number | null;
	startG: number | null;
	startStan: StanZywy | 'brak';
	startKierunek: Kierunek | null;
	cena: number;
	minCena: number;
	maxCena: number;
	maxZc: number | null;
	maxZt: number | null;
	/** Ostatnia obserwacja (unix ms). */
	aktualizacja: number;
	dziennik: WpisPomiaru[];
	migawka: Migawka | null;
}

/** Bieżący odczyt pomiaru (nie zapisujemy — liczony z danych). */
export interface OdczytPomiaru {
	trend: TrendWidok | null;
	korekta: KorektaZywa | null;
	/** Analiza czeka na świeżo zamkniętą świecę. */
	czeka: boolean;
	/** Ostatnia zamknięta świeca analizy (unix s) i cena teraz (ceny na żywo albo zamknięcie). */
	ostatnia: number | null;
	cena: number | null;
}

interface DaneRynku {
	trend: TrendWidok | null;
	ostatnia: number;
	cena: number;
}

/**
 * „Pomiar na żywo”: użytkownik startuje pomiar krypto na ramie, a usługa (w całym panelu,
 * nie tylko na stronie modułu) co 6 s z cen na żywo i co minutę z lekkiej analizy zapisuje,
 * co się stało od startu: zbliżenie do rekordu, przebicie progu w trakcie świecy (i cofnięcie),
 * rekord ceny / czasu potwierdzony zamknięciem, nowy szczyt korekty, koniec trendu. Pomiary
 * w localStorage — po powrocie potwierdzenia z zamknięć uzupełniają się z analizy; przebić
 * w trakcie świecy, gdy portal był zamknięty, nie da się odtworzyć (od tego są alerty).
 */
@Injectable({ providedIn: 'root' })
export class ObPomiarService {
	private readonly _api = inject(ApiService);
	private readonly _socket = inject(SocketService);

	readonly pomiary = signal<Pomiar[]>(wczytaj());
	readonly aktywne = computed(() => this.pomiary().filter((p) => p.koniec === null));
	private readonly _dane = signal<Map<string, DaneRynku>>(new Map());
	/** Świeca w toku per (symbol, rama) — z cen obserwowanych od startu strony. */
	private readonly _swiece = new Map<string, { t: number; h: number; l: number }>();
	private readonly _ceny = signal<Map<string, number>>(new Map());
	private readonly _teraz = signal(Date.now());

	constructor() {
		this._socket.binanceTickerList.pipe(takeUntilDestroyed()).subscribe((lista) => {
			if (!this.aktywne().length) {
				return;
			}

			const potrzebne = new Set(this.aktywne().map((p) => p.symbol));
			const m = new Map<string, number>();

			for (const t of lista) {
				if (potrzebne.has(t.s) && t.c > 0) {
					m.set(t.s, t.c);
				}
			}

			this._ceny.set(m);
			this._teraz.set(Date.now());
			this._obserwuj();
		});

		interval(ODSWIEZ_MS)
			.pipe(startWith(0), takeUntilDestroyed())
			.subscribe(() => this._pobierz());
	}

	/** Odczyt na żywo jednego pomiaru (do kart). */
	odczyt(p: Pomiar): OdczytPomiaru {
		const d = this._dane().get(klucz(p.symbol, p.rama));
		const k = this._korekta(p, d);
		return {
			trend: d?.trend ?? null,
			korekta: k,
			czeka: !!d?.trend?.biezaca && !k,
			ostatnia: d?.ostatnia ?? null,
			cena: this._ceny().get(p.symbol) ?? d?.cena ?? null,
		};
	}

	/** Start pomiaru: zapisuje stan z tej chwili (trend i korekta, jeśli strona je zna). */
	start(
		symbol: string,
		rama: Rama,
		trend: TrendWidok | null,
		ostatnia: number,
		cena: number,
	): void {
		const teraz = Date.now();
		const d: DaneRynku = { trend, ostatnia, cena };
		this._dane.update((m) => new Map(m).set(klucz(symbol, rama), d));
		const k = korektaNaZywo(
			trend,
			SEKUNDY_RAMY[rama],
			ostatnia,
			swiecaZCeny(rama, cena, teraz),
			teraz / 1000,
		);
		const p: Pomiar = {
			id: `${symbol}-${rama}-${teraz}`,
			symbol,
			rama,
			start: teraz,
			koniec: null,
			startCena: cena,
			startZc: k?.zuzycieCeny ?? null,
			startZt: k?.zuzycieCzasu ?? null,
			startG: k?.glebokosc ?? null,
			startStan: k?.stan ?? (trend ? 'zbyt_wczesnie' : 'brak'),
			startKierunek: trend?.kierunek ?? null,
			cena,
			minCena: cena,
			maxCena: cena,
			maxZc: k?.zuzycieCeny ?? null,
			maxZt: k?.zuzycieCzasu ?? null,
			aktualizacja: teraz,
			dziennik: [
				{
					t: teraz,
					typ: 'start',
					cena,
					potw: true,
					zc: k?.zuzycieCeny ?? null,
					zt: k?.zuzycieCzasu ?? null,
					g: k?.glebokosc,
					kier: trend?.kierunek,
				},
			],
			migawka: migawka(trend, k),
		};
		this._zapisz([p, ...this.pomiary()].slice(0, MAX_POMIAROW));
		this._pobierz();
	}

	zakoncz(id: string): void {
		this._zmien(id, (p) => ({
			...p,
			koniec: Date.now(),
			dziennik: dopisz(p.dziennik, { t: Date.now(), typ: 'stop', cena: p.cena, potw: true }),
		}));
	}

	usun(id: string): void {
		this._zapisz(this.pomiary().filter((p) => p.id !== id));
	}

	/** Trwający pomiar tej krypto na tej ramie (do przycisku „Mierz na żywo”). */
	trwajacy(symbol: string, rama: Rama): Pomiar | null {
		return this.aktywne().find((p) => p.symbol === symbol && p.rama === rama) ?? null;
	}

	private _korekta(p: Pomiar, d: DaneRynku | undefined): KorektaZywa | null {
		if (!d || p.koniec !== null) {
			return null;
		}

		const iv = SEKUNDY_RAMY[p.rama];
		const teraz = this._teraz() / 1000;
		const cena = this._ceny().get(p.symbol) ?? d.cena;
		const start = teraz - (teraz % iv);
		const k = klucz(p.symbol, p.rama);
		let sw = this._swiece.get(k);

		if (!sw || sw.t !== start) {
			sw = { t: start, h: cena, l: cena };
			this._swiece.set(k, sw);
		}

		sw.h = Math.max(sw.h, cena);
		sw.l = Math.min(sw.l, cena);
		return korektaNaZywo(
			d.trend,
			iv,
			d.ostatnia,
			{ t: start, o: cena, h: sw.h, l: sw.l, c: cena },
			teraz,
		);
	}

	/** Lekka analiza każdej mierzonej pary (symbol, rama). */
	private _pobierz(): void {
		const pary = [...new Set(this.aktywne().map((p) => klucz(p.symbol, p.rama)))];

		if (!pary.length) {
			return;
		}

		forkJoin(
			pary.map((k) => {
				const [s, r] = k.split('|');
				return this._api
					.sendGET<OverbalanceResponse>(
						`${ApiRequest.MarketOverbalance}?symbol=${s}&interval=${r}&tryb=lekki`,
					)
					.pipe(
						map((d) => [k, d] as const),
						catchError(() => of([k, null] as const)),
					);
			}),
		).subscribe((wyniki) => {
			const m = new Map(this._dane());

			for (const [k, d] of wyniki) {
				if (d) {
					m.set(k, {
						trend: d.analiza.trend,
						ostatnia: d.analiza.ostatnia_t,
						cena: d.analiza.cena,
					});
				}
			}

			this._dane.set(m);
			this._teraz.set(Date.now());
			this._obserwuj();
		});
	}

	/** Porównanie z poprzednią obserwacją każdego pomiaru → wpisy dziennika i statystyki. */
	private _obserwuj(): void {
		let zmiana = false;
		const teraz = this._teraz();
		const nowe = this.pomiary().map((p) => {
			if (p.koniec !== null) {
				return p;
			}

			const d = this._dane().get(klucz(p.symbol, p.rama));
			const cena = this._ceny().get(p.symbol);

			if (!d) {
				return p;
			}

			const k = this._korekta(p, d);
			const c = cena ?? d.cena;
			const iv = SEKUNDY_RAMY[p.rama] * 1000;
			const m = migawka(d.trend, k);
			const st = p.migawka;
			let dz = p.dziennik;
			const wpis = (typ: TypWpisu, potw: boolean, t = teraz): void => {
				dz = dopisz(dz, {
					t,
					typ,
					cena: c,
					potw,
					zc: k?.zuzycieCeny ?? null,
					zt: k?.zuzycieCzasu ?? null,
					g: k?.glebokosc,
					kier: d.trend?.kierunek,
				});
			};

			if (st) {
				if (st.trendOd !== m.trendOd || st.kierunek !== m.kierunek) {
					if (st.trendOd !== null) {
						wpis('koniec_trendu', true, d.ostatnia * 1000 + iv);
					}

					if (m.trendOd !== null) {
						wpis('nowy_trend', true, d.ostatnia * 1000 + iv);
					}
				} else {
					if (
						st.korektaOd !== null &&
						m.korektaOd !== null &&
						st.korektaOd !== m.korektaOd
					) {
						wpis('nowy_szczyt', true, d.ostatnia * 1000 + iv);
					}

					if (st.obCena === null && m.obCena !== null) {
						wpis('rekord_ceny', true, m.obCena * 1000 + iv);
					} else if (!st.przebita && m.przebita) {
						wpis('przebicie', false);
					} else if (st.przebita && !m.przebita && m.obCena === null) {
						wpis('cofniecie', false);
					}

					if (st.obCzas === null && m.obCzas !== null) {
						wpis('rekord_czasu', true, m.obCzas * 1000 + iv);
					}

					if (!st.blisko && m.blisko && m.obCena === null && !m.przebita) {
						wpis('blisko', false);
					}

					if (!st.zagrozenie && m.zagrozenie) {
						wpis('zagrozenie', false);
					}
				}
			}

			const maks = (a: number | null, b: number | null | undefined): number | null =>
				b == null ? a : a === null ? b : Math.max(a, b);
			const nowy: Pomiar = {
				...p,
				cena: c,
				minCena: Math.min(p.minCena, c),
				maxCena: Math.max(p.maxCena, c),
				maxZc: maks(p.maxZc, k?.zuzycieCeny),
				maxZt: maks(p.maxZt, k?.zuzycieCzasu),
				aktualizacja: teraz,
				dziennik: dz,
				migawka: m,
			};
			zmiana = true;
			return nowy;
		});

		if (zmiana) {
			this._zapisz(nowe);
		}
	}

	private _zmien(id: string, fn: (p: Pomiar) => Pomiar): void {
		this._zapisz(this.pomiary().map((p) => (p.id === id ? fn(p) : p)));
	}

	private _zapisz(p: Pomiar[]): void {
		this.pomiary.set(p);

		try {
			localStorage.setItem(KLUCZ, JSON.stringify(p));
		} catch {
			/* tryb prywatny — pomiar trwa do zamknięcia karty */
		}
	}
}

function klucz(symbol: string, rama: Rama): string {
	return `${symbol}|${rama}`;
}

function swiecaZCeny(rama: Rama, cena: number, terazMs: number): SwiecaZywa {
	const iv = SEKUNDY_RAMY[rama];
	const s = terazMs / 1000;
	const t = s - (s % iv);
	return { t, o: cena, h: cena, l: cena, c: cena };
}

export function migawka(trend: TrendWidok | null, k: KorektaZywa | null): Migawka {
	const b = trend?.biezaca ?? null;
	return {
		trendOd: trend?.od_t ?? null,
		kierunek: trend?.kierunek ?? null,
		korektaOd: b?.od_t ?? null,
		obCena: b?.ob_cena_t ?? null,
		obCzas: b?.ob_czas_t ?? null,
		przebita: !!k?.przebitaCena,
		zagrozenie: !!k?.zagrozenieKonca,
		blisko: Math.max(k?.zuzycieCeny ?? 0, k?.zuzycieCzasu ?? 0) >= PROG_BLISKO,
	};
}

function dopisz(dz: WpisPomiaru[], w: WpisPomiaru): WpisPomiaru[] {
	return [...dz, w].slice(-MAX_WPISOW);
}

function wczytaj(): Pomiar[] {
	try {
		const v = JSON.parse(localStorage.getItem(KLUCZ) ?? '[]');
		return Array.isArray(v) ? v.filter((p) => p && typeof p.symbol === 'string') : [];
	} catch {
		return [];
	}
}
